// @mui material components
import {
  Snackbar,
  Alert,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  Button,
  ListItem,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDButton from "theme/components/MDButton";
import MDInput from "theme/components/MDInput";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios.js";
import DataTable from "theme/Tables/DataTable";
import usePageTitle from "app/pageTitle";
import AppConfig from "appConfig";

function DawVisitsPage() {
  const pageTitle = "Display & Win - Retailer Visits";
  usePageTitle(pageTitle);
  const navigate = useNavigate();
  const { ref } = useParams();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [refreshPage, setRefreshPage] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);

  const [visits, setVisits] = useState([]);
  const [visit, setVisit] = useState(null);

  const tableColumns = [
    { Header: "#", accessor: "gps", align: "left" },
    { Header: "Retailer", accessor: "retailer", align: "left" },
    { Header: "Agent", accessor: "agent", align: "left" },
    { Header: "Visited On", accessor: "created", align: "right" },
  ];

  const buildRateRows = (data) => {
    const rows = [];
    data.map((row) => {
      rows.push({
        retailer: row.retailer,
        agent: row.agent,
        gps: (
          <MDBox>
            <MDButton
              size="small"
              variant="outlined"
              color="info"
              iconOnly
              onClick={() => {
                getVisit(row.reference);
              }}
            >
              <Icon sx={{ cursor: "pointer" }} fontSize="small" color="info">
                visibility
              </Icon>
            </MDButton>
          </MDBox>
        ),
        created: row.created,
      });
      return null;
    });

    return rows;
  };

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get(`portal/daw/${ref}/visits.json`)
        .then((result) => {
          // console.log(result);
          setShowLoading(false);
          if (result.data.status === "success") {
            const rows = buildRateRows(result.data.data.visits);
            setVisits(rows);

            setPageLoaded(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );
          setOpen(true);
        });
    };

    fetchData();
  }, [refreshPage]);

  async function getVisit(reference) {
    setShowLoading(true);
    await axios
      .get(`portal/daw/visits/${reference}/fetch.json`)
      .then((result) => {
        // console.log(result);
        setShowLoading(false);
        if (result.data.status === "success") {
          setVisit(result.data.data.visit);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      {visit && (
        <Dialog open fullWidth maxWidth="sm">
          <DialogTitle></DialogTitle>
          <DialogContent>
            <MDBox>
              <MDBox align="center">
                <MDTypography variant="subtitle2">Retailer</MDTypography>
              </MDBox>
              <MDBox align="center">
                <MDTypography variant="h6">{visit.retailer}</MDTypography>
              </MDBox>
              <MDBox mt={2} align="center">
                <img src={`${AppConfig.api_media_url}${visit.image}`} />
              </MDBox>
              <MDBox mt={3}>
                {visit.products.map((row) => (
                  <MDBox mb={2}>
                    <ListItem divider>
                      <ListItemText
                        primary={<MDTypography variant="subtitle2">{row.name}</MDTypography>}
                        secondary={
                          <MDTypography
                            variant="subtitle2"
                            fontSize={12}
                          >{`Displayed Quantity: ${row.quantity}`}</MDTypography>
                        }
                      />
                      <MDBox align="center">
                        <MDTypography variant="caption">Points</MDTypography>
                        <MDTypography variant="h6">{row.points}</MDTypography>
                      </MDBox>
                    </ListItem>
                  </MDBox>
                ))}
              </MDBox>
              <MDBox mt={3}>
                <ListItem divider>
                  <ListItemText
                    primary={<MDTypography variant="subtitle2">GPS</MDTypography>}
                    secondary={
                      <MDTypography
                        variant="subtitle2"
                        fontSize={12}
                      >{`Lat: ${visit.lat} | Lng: ${visit.lng}`}</MDTypography>
                    }
                  />
                  <MDBox align="center">
                    <MDButton
                      size="small"
                      variant="outlined"
                      color="error"
                      iconOnly
                      onClick={() => {
                        const url = `https://maps.google.com?q=${visit.lat},${visit.lng}`;
                        window.open(url, "_blank").focus();
                      }}
                    >
                      <Icon sx={{ cursor: "pointer" }} fontSize="small" color="error">
                        location_on
                      </Icon>
                    </MDButton>
                  </MDBox>
                </ListItem>
              </MDBox>
              <MDBox mt={3}>
                <ListItem divider>
                  <ListItemText
                    primary={<MDTypography variant="subtitle2">Visited On</MDTypography>}
                    secondary={
                      <MDTypography variant="subtitle2" fontSize={12}>
                        {visit.created}
                      </MDTypography>
                    }
                  />
                </ListItem>
              </MDBox>
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton
              variant="outlined"
              color="dark"
              iconOnly
              circular
              onClick={() => setVisit(null)}
            >
              <Icon>close</Icon>
            </MDButton>
          </DialogActions>
        </Dialog>
      )}
      <MDBox mb={5}>
        <MDBox mt={1} mb={2} display="flex" justifyContent="space-between">
          <MDBox>
            <Button
              color="dark"
              size="small"
              startIcon={<Icon>west</Icon>}
              onClick={() => {
                navigate(`/trade-marketing/promotions/display-win/${ref}`);
              }}
            >
              Back
            </Button>
          </MDBox>
        </MDBox>
        {pageLoaded && (
          <MDBox mt={2}>
            <Card sx={{ height: "100%", marginTop: "0px" }}>
              <MDBox p={0}>
                <MDBox display="flex" justifyContent="space-between" alignItems="center">
                  <MDBox p={2}>
                    <MDTypography variant="h6" fontWeight="medium">
                      Retailer Visits
                    </MDTypography>
                  </MDBox>
                </MDBox>
                <MDBox mt={1}>
                  <DataTable
                    table={{ columns: tableColumns, rows: visits }}
                    isSorted
                    entriesPerPage
                    showTotalEntries
                    noEndBorder
                  />
                </MDBox>
              </MDBox>
            </Card>
          </MDBox>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default DawVisitsPage;
